// Per-PSP backend integration walkthrough.
//
// Rendered in two surfaces: the admin /integrations page (operator view)
// and the merchant /m/integrations page (developer view). The content is
// identical — both audiences are looking at "what does my server have to
// call when this PSP is wired up?". The component takes no props on
// purpose; everything that varies between admin/merchant lives in the
// route shell (chrome, breadcrumbs).
//
// Content is intentionally English-only: it's reference material with
// code samples that paste verbatim. The route shell handles the page
// title & subtitle in both locales; everything below stays in EN so the
// curl/JSON examples don't get bidi-flipped.
//
// Source-of-truth for behaviour: app/gateways/<psp>/adapter.py and
// app/services/payment_service.py. If you change the orchestrator's
// shape (e.g. new next_action variant) update the matching tab here.

import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  CreditCard,
  Globe,
  KeyRound,
  Lightbulb,
  ListChecks,
  QrCode,
  Send,
  ShieldCheck,
  Smartphone,
  TerminalSquare,
} from 'lucide-react';
import * as React from 'react';

import { PspLogo } from '@/features/merchant/psp-brand';
import type { PspCode } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export function PspGuide() {
  return (
    <div className="space-y-6">
      <SharedSection />
      <Card>
        <CardHeader className="flex-row items-center gap-3 space-y-0">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
            <CreditCard className="h-4 w-4" />
          </div>
          <CardTitle>Per-PSP flows</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="sadad">
            <TabsList className="h-auto flex-wrap gap-1 p-1">
              <PspTab psp="sadad" label="Sadad" flow="OTP" />
              <PspTab psp="moamalat" label="Moamalat" flow="LightBox" />
              <PspTab psp="mobicash" label="Mobicash" flow="QR" />
              <PspTab psp="tlync" label="T-Lync" flow="Redirect" />
              <PspTab psp="adfali" label="Adfali" flow="Roadmap" />
            </TabsList>

            <TabsContent value="sadad"><SadadGuide /></TabsContent>
            <TabsContent value="moamalat"><MoamalatGuide /></TabsContent>
            <TabsContent value="mobicash"><MobicashGuide /></TabsContent>
            <TabsContent value="tlync"><TlyncGuide /></TabsContent>
            <TabsContent value="adfali"><AdfaliGuide /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function PspTab({ psp, label, flow }: { psp: PspCode; label: string; flow: string }) {
  return (
    <TabsTrigger value={psp} className="gap-2 px-3">
      <PspLogo psp={psp} size="sm" />
      <span className="font-medium">{label}</span>
      <span className="hidden text-[10px] text-muted-foreground sm:inline">· {flow}</span>
    </TabsTrigger>
  );
}

// ---------- shared (auth, headers, idempotency, signing) ----------

function SharedSection() {
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-3 space-y-0">
        <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
          <KeyRound className="h-4 w-4" />
        </div>
        <CardTitle>Before you start: shared mechanics</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <Item icon={KeyRound} label="Authenticate every request">
          Every call to <Code>/v1/*</Code> carries an <Code>X-API-Key</Code> header issued
          for one merchant. Store it server-side; never embed it in mobile apps or
          browser bundles. Rotate via the merchant portal &rarr; <em>API keys</em>; revocation
          is instant.
          <Pre>
            {`POST /v1/payments HTTP/1.1
Host: app.payhub.ly
X-API-Key: pk_live_…
Content-Type: application/json
Idempotency-Key: 5b1b7d70-…  ← optional but strongly recommended`}
          </Pre>
        </Item>

        <Item icon={ListChecks} label="One initiate, one of four next-actions">
          <Code>POST /v1/payments</Code> always returns a <Code>next_action</Code> object
          (when the PSP needs more interaction) plus a stable <Code>id</Code>. The PSP
          dictates which variant you receive:
          <ul className="mt-2 list-disc space-y-1 ps-5">
            <li><Code>otp_required</Code> — your backend re-prompts the customer (Sadad, Adfali).</li>
            <li>
              <Code>lightbox</Code> — open Moamalat's hosted iframe with the returned params.
            </li>
            <li><Code>qr</Code> — display the QR string (Mobicash); customer authorises in their wallet.</li>
            <li><Code>redirect</Code> — 302 the customer to a PSP-hosted checkout (T-Lync).</li>
          </ul>
        </Item>

        <Item icon={ShieldCheck} label="Verify outbound webhook signatures">
          PayHub signs every outbound delivery as
          <Code>Hub-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</Code> over the
          <em> raw </em> request body. Compare in constant time against
          <Code>HMAC-SHA256(secret, &quot;{`{t}.{body}`}&quot;)</Code>; reject if
          <Code>|now − t| &gt; 300s</Code>.
          <Pre>
            {`# Python (Stripe-style; same scheme PayHub uses)
import hmac, hashlib, time

def verify(body: bytes, header: str, secret: bytes) -> bool:
    parts = dict(p.split("=", 1) for p in header.split(","))
    t, sig = int(parts["t"]), parts["v1"]
    if abs(time.time() - t) > 300:
        return False
    expected = hmac.new(
        secret, f"{t}.".encode() + body, hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, sig)`}
          </Pre>
          Webhook bodies arrive as canonical JSON (sorted keys, no whitespace).
          Each delivery also carries <Code>X-PayHub-Event</Code> (e.g.
          <Code>succeeded</Code>) and <Code>X-PayHub-Delivery</Code> (UUID, useful
          for idempotent processing).
        </Item>

        <Item icon={Send} label="Idempotency keys">
          Send a unique <Code>Idempotency-Key</Code> on every initiate &amp; refund.
          Replays within 24 h return the original response without re-hitting the PSP.
          Pick a UUID per <em>commercial intent</em>, not per HTTP retry — that way
          both your client retries <em>and</em> a queued worker retry collapse onto
          the same row.
        </Item>

        <Item icon={Lightbulb} label="Handle terminal status only on webhooks (or polling)">
          Don't trust the customer's browser to tell you the payment succeeded —
          they can close the tab, lose signal, or land on the cancel URL by
          accident. Wait for the <Code>payment.succeeded</Code> webhook (or, for
          PSPs without one, the next status poll) before fulfilment. Status
          progression: <Code>pending → requires_action → processing → succeeded |
          failed | expired</Code>. Refunds transition <Code>succeeded → refunded</Code>.
        </Item>

        <Item icon={Globe} label="Webhook event types">
          Endpoints subscribe to a list of event types or <Code>*</Code> for all.
          The full vocabulary today: <Code>initiated</Code>,
          <Code>otp_required</Code>, <Code>otp_submitted</Code>,
          <Code>redirected</Code>, <Code>qr_issued</Code>,
          <Code>lightbox_issued</Code>, <Code>gateway_ack</Code>,
          <Code>gateway_rejected</Code>, <Code>webhook_received</Code>,
          <Code>polled</Code>, <Code>succeeded</Code>, <Code>failed</Code>,
          <Code>expired</Code>, <Code>refunded</Code>, <Code>replayed</Code>.
        </Item>
      </CardContent>
    </Card>
  );
}

// ---------- Sadad ----------

function SadadGuide() {
  return (
    <PspBody
      psp="sadad"
      flow="OTP — Almadar wallet"
      icon={Smartphone}
      summary={
        <>
          The customer enters their MSISDN at your checkout. PayHub asks Sadad to
          push an OTP to that number; you collect the OTP and submit it back. No
          inbound webhook from Sadad — PayHub finalises on OTP submission and a
          background poller backstops any stragglers.
        </>
      }
      credentials={[
        { name: 'auth_token', kind: 'secret', detail: 'Bearer token issued by Almadar.' },
        { name: 'category', kind: 'number', detail: 'Almadar merchant category (defaults to 20).' },
      ]}
      steps={[
        {
          title: '1. Configure the gateway in PayHub',
          body: (
            <>
              In the merchant portal &rarr; <em>Gateways</em>, choose <em>Sadad</em>.
              Paste the bearer token Almadar issued you and confirm the category.
              Hit <em>Test</em> — PayHub runs a non-mutating preflight against the
              Sadad endpoint to surface bad creds before the first real charge.
            </>
          ),
        },
        {
          title: '2. Initiate from your backend with the customer MSISDN',
          body: (
            <>
              The MSISDN goes inside <Code>customer.msisdn</Code> in international
              format (<Code>2189…</Code>). PayHub hashes it with the per-install
              pepper for indexing and forwards plain-text only to Sadad.
              <Pre>
                {`POST /v1/payments
{
  "psp": "sadad",
  "merchant_order_ref": "ORD-2026-00042",
  "amount_minor": 4500,             // 45.00 LYD
  "currency": "LYD",
  "customer": { "msisdn": "218910000000" },
  "metadata": { "cart_id": "c_8821" }
}`}
              </Pre>
              Response:
              <Pre>
                {`201 Created
{
  "id": "f7d6…-uuid",
  "status": "requires_action",
  "psp": "sadad",
  "psp_ref": "SADAD-7781234",
  "next_action": {
    "type": "otp_required",
    "masked_destination": "218 91 *** 0000"
  },
  "amount_minor": 4500,
  "currency": "LYD",
  "merchant_order_ref": "ORD-2026-00042"
}`}
              </Pre>
            </>
          ),
        },
        {
          title: '3. Prompt for the OTP and submit',
          body: (
            <>
              Render an OTP form. When the customer types the code, submit it
              against the payment ID PayHub returned:
              <Pre>
                {`POST /v1/payments/{id}/otp
{ "code": "482931" }

→ 200 OK
{
  "id": "f7d6…-uuid",
  "status": "succeeded",
  "psp": "sadad",
  "psp_ref": "SADAD-7781234",
  ...
}`}
              </Pre>
              A wrong code keeps the payment in <Code>requires_action</Code> and
              returns a <Code>402</Code> with a translatable error message — let
              the user retry. PayHub does not auto-retry OTP submissions.
            </>
          ),
        },
        {
          title: '4. Listen for the webhook (or poll if you skip webhooks)',
          body: (
            <>
              Sadad does not call back, but PayHub still emits the standard
              outbound events to <em>your</em> webhook endpoints. Subscribe to
              <Code>succeeded</Code>, <Code>failed</Code>, and <Code>expired</Code>.
              For high-stakes flows you can also poll <Code>GET /v1/payments/{`{id}`}</Code>
              — PayHub's <em>sadad_poller</em> already runs every 30s for
              in-flight Sadad rows, so the projection you read is fresh.
            </>
          ),
        },
      ]}
      pitfalls={[
        'Sadad rejects non-Almadar MSISDNs. Validate the prefix client-side before hitting /v1/payments to avoid a noisy GATEWAY_REJECTED.',
        'OTP codes expire after a short window (Almadar default ~3 min). If your UI lets the customer pause, gracefully re-initiate rather than re-submitting a stale code.',
        'Refunds are not yet supported by Sadad. supports_refund=false in the capability struct; expect 422 from POST /v1/payments/{id}/refund.',
      ]}
    />
  );
}

// ---------- Moamalat ----------

function MoamalatGuide() {
  return (
    <PspBody
      psp="moamalat"
      flow="Hosted fields — Moamalat LightBox"
      icon={CreditCard}
      summary={
        <>
          Card-present capture via Moamalat's <em>LightBox</em> iframe. Your page
          mounts the iframe with signed parameters PayHub returns; the PAN/CVV
          never touch your servers (PCI SAQ-A scope). Settlement is
          reconciled from the Filter API on the nightly puller (02:15 UTC) or
          on demand from the Settlements page.
        </>
      }
      credentials={[
        { name: 'merchant_id', detail: 'Issued by Moamalat onboarding.' },
        { name: 'terminal_id', detail: 'Per-terminal — one PayHub gateway row per terminal.' },
        { name: 'secret_key_hex', kind: 'secret', detail: 'HMAC key, hex-encoded.' },
        { name: 'currency_code', detail: 'ISO 4217 numeric — 434 for LYD.' },
        { name: 'lightbox_env', detail: '"test" or "production" — flips the LightBox script URL.' },
      ]}
      steps={[
        {
          title: '1. Configure the terminal',
          body: (
            <>
              Configure <em>Moamalat</em> in the gateway tab with the merchant ID,
              terminal ID, currency (<Code>434</Code> for LYD) and the hex secret.
              The <em>Test</em> button issues a Filter request with a 0-row window
              to verify HMAC canonicalisation without moving money.
            </>
          ),
        },
        {
          title: '2. Initiate to obtain signed LightBox params',
          body: (
            <>
              <Pre>
                {`POST /v1/payments
{
  "psp": "moamalat",
  "merchant_order_ref": "INV-9001",
  "amount_minor": 12000,
  "currency": "LYD",
  "return_urls": {
    "success_url": "https://shop.example.ly/orders/9001/ok",
    "cancel_url":  "https://shop.example.ly/orders/9001/cancelled"
  }
}`}
              </Pre>
              Response carries a <Code>lightbox</Code> next-action containing
              every parameter the LightBox script expects, already HMAC-signed:
              <Pre>
                {`{
  "id": "1e0f…",
  "status": "requires_action",
  "psp": "moamalat",
  "next_action": {
    "type": "lightbox",
    "params": {
      "MerchantId": "1234",
      "TerminalId": "12345001",
      "MerchantReference": "1e0f…",
      "Amount": "12000",
      "CurrencyCode": "434",
      "DateTimeLocalTrxn": "2026…",
      "SecureHash": "8f7e…"
    }
  }
}`}
              </Pre>
            </>
          ),
        },
        {
          title: '3. Mount the LightBox iframe on your checkout page',
          body: (
            <>
              Load Moamalat's LightBox script (URL flips with <Code>lightbox_env</Code>),
              then call its constructor with the params dict verbatim. Don't mutate
              field names or recompute the hash — the signature is over Moamalat's
              ordered canonicalisation, and PayHub already produced the right one.
              <Pre>
                {`<script src="https://npg.moamalat.net:6006/js/lightbox.js"></script>
<div id="lightbox-container"></div>
<script>
  const params = {/* response.next_action.params */};
  Lightbox.Checkout.configure = params;
  Lightbox.Checkout.configure.completeCallback = function (resp) {
    // resp comes from Moamalat — DO NOT trust it for fulfilment.
    // Wait for the PayHub webhook on your server side.
    window.location = '/orders/9001/processing';
  };
  Lightbox.Checkout.showLightbox();
</script>`}
              </Pre>
            </>
          ),
        },
        {
          title: '4. Receive the inbound webhook',
          body: (
            <>
              Moamalat posts back to{' '}
              <Code>{'/v1/callbacks/moamalat/{merchant_id}'}</Code>; PayHub verifies
              the SecureHash with one of three Moamalat canonicalisations (the
              adapter picks the one that matches), persists a
              <Code>WEBHOOK_RECEIVED</Code> event, then transitions the payment
              and fans out your outbound webhook with
              <Code>X-PayHub-Event: succeeded</Code> (or <Code>failed</Code>).
              Match by <Code>merchant_ref</Code> — Moamalat does not always echo
              its own transaction ID.
            </>
          ),
        },
        {
          title: '5. Reconcile via the Filter API',
          body: (
            <>
              At 02:15 UTC each night PayHub pulls yesterday's Filter window and
              writes a settlement file. From the merchant portal
              &rarr; <em>Settlements</em> you can click <em>Pull from PSP</em> for an
              ad-hoc window. File hashes are deduped; re-running the same window
              is a safe no-op.
            </>
          ),
        },
      ]}
      pitfalls={[
        'Test vs production: the lightbox_env field decides the JS URL the iframe loads from. Mismatched env = "Invalid hash" with no useful detail.',
        'amount_minor is integer minor units; LYD has 3 decimals so 12.00 LYD is 12000, not 1200.',
        'If your shop is multi-currency, configure one PayHub gateway row per (currency, terminal) pair — Moamalat ties the hash to currency_code.',
      ]}
    />
  );
}

// ---------- Mobicash ----------

function MobicashGuide() {
  return (
    <PspBody
      psp="mobicash"
      flow="QR — wallet authorisation"
      icon={QrCode}
      summary={
        <>
          PayHub asks Mobicash to mint an authorisation; the response includes a
          QR payload. You render the QR and the customer authorises inside the
          Mobicash app. Settlement comes via webhook.
        </>
      }
      credentials={[
        { name: 'merchant_code', detail: 'Mobicash merchant identifier.' },
        { name: 'api_key', kind: 'secret', detail: 'Issued by Mobicash; rotate from the merchant portal.' },
        { name: 'webhook_secret', kind: 'secret', detail: 'HMAC secret — Mobicash signs callbacks with it.' },
      ]}
      steps={[
        {
          title: '1. Configure Mobicash',
          body: (
            <>
              Paste merchant code, API key, and webhook secret in the gateway tab.
              The webhook secret is what Mobicash itself uses to sign callbacks —
              it's distinct from the secret you set on your <em>outbound</em>
              endpoint.
            </>
          ),
        },
        {
          title: '2. Initiate and surface the QR',
          body: (
            <>
              <Pre>
                {`POST /v1/payments
{
  "psp": "mobicash",
  "merchant_order_ref": "BAR-2026-118",
  "amount_minor": 2500,
  "currency": "LYD"
}`}
              </Pre>
              Response includes a <Code>qr</Code> next-action with the EMVCo
              payload you pass straight to a QR renderer:
              <Pre>
                {`{
  "status": "requires_action",
  "next_action": {
    "type": "qr",
    "reference": "MBC-998877",
    "qr_payload": "00020101021226…6304ABCD",
    "expires_at": "2026-05-05T13:42:00Z"
  }
}`}
              </Pre>
            </>
          ),
        },
        {
          title: '3. Wait for the customer to authorise',
          body: (
            <>
              Two ways to know it cleared:
              <ul className="mt-2 list-disc space-y-1 ps-5">
                <li>
                  <strong>Push:</strong> subscribe your outbound webhook to
                  <Code>succeeded</Code> / <Code>failed</Code> / <Code>expired</Code>.
                </li>
                <li>
                  <strong>Pull:</strong> long-poll <Code>GET /v1/payments/{`{id}`}</Code>
                  every 2–3 s. Cap at <Code>expires_at</Code> and surface a friendly
                  &quot;QR expired&quot; in the UI.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: '4. Mobicash → PayHub webhook',
          body: (
            <>
              Mobicash posts JSON to{' '}
              <Code>{'/v1/callbacks/mobicash/{merchant_id}'}</Code>; PayHub verifies
              the HMAC with the webhook secret, matches by
              <Code>psp_ref</Code> (Mobicash always echoes its own transaction ID),
              and transitions to <Code>succeeded</Code>. Your outbound delivery is
              enqueued in the same DB tx — &quot;ghost deliveries&quot; are
              impossible.
            </>
          ),
        },
      ]}
      pitfalls={[
        'QR payloads are time-bound. If your customer parks at the till, re-initiate rather than re-rendering the same QR — Mobicash will reject the second scan.',
        'Mobile network reordering can cause the webhook to arrive after a manual /v1/payments/{id} GET shows succeeded; build for both orderings.',
      ]}
    />
  );
}

// ---------- T-Lync ----------

function TlyncGuide() {
  return (
    <PspBody
      psp="tlync"
      flow="Redirect — PSP-hosted checkout"
      icon={Globe}
      summary={
        <>
          Customer 302s off your domain to the T-Lync hosted page, completes the
          payment, and is bounced back to your <Code>success_url</Code> /
          <Code>cancel_url</Code>. T-Lync also POSTs a form-encoded callback to
          PayHub server-to-server — that's the source of truth, not the browser
          return.
        </>
      }
      credentials={[
        { name: 'merchant_id', detail: 'T-Lync merchant identifier.' },
        { name: 'merchant_secret', kind: 'secret', detail: 'Used to sign initiate requests and verify callbacks.' },
        { name: 'callback_token', kind: 'secret', detail: 'Shared token T-Lync echoes on the form callback.' },
      ]}
      steps={[
        {
          title: '1. Configure T-Lync',
          body: (
            <>
              Paste merchant ID, secret, and callback token. Set return URLs at
              gateway-config time <em>or</em> per-payment via <Code>return_urls</Code>;
              per-payment wins.
            </>
          ),
        },
        {
          title: '2. Initiate and redirect the customer',
          body: (
            <>
              <Pre>
                {`POST /v1/payments
{
  "psp": "tlync",
  "merchant_order_ref": "ORD-44128",
  "amount_minor": 7500,
  "currency": "LYD",
  "return_urls": {
    "success_url": "https://shop.example.ly/orders/44128/ok",
    "cancel_url":  "https://shop.example.ly/orders/44128/cancelled"
  },
  "customer": { "email": "buyer@example.ly" }
}`}
              </Pre>
              Response includes a <Code>redirect</Code> next-action. Send the
              browser there:
              <Pre>
                {`{
  "status": "requires_action",
  "next_action": {
    "type": "redirect",
    "method": "GET",
    "url": "https://checkout.tlync.ly/pay/abcd1234"
  }
}`}
              </Pre>
              For POST-redirects, render the returned <Code>fields</Code> as a
              hidden-input auto-submit form. Don't mutate them.
            </>
          ),
        },
        {
          title: '3. Treat the browser return as informational only',
          body: (
            <>
              <Code>success_url</Code> can be hit by a sneaker bot, a back-button,
              or someone who closed the tab and re-opened from history. <strong>Do
              not</strong> fulfil the order on that hit. Show
              &quot;Verifying&hellip;&quot; and poll
              <Code>GET /v1/payments/{`{id}`}</Code> until <Code>status</Code>
              becomes terminal.
            </>
          ),
        },
        {
          title: '4. Server-to-server callback (T-Lync → PayHub)',
          body: (
            <>
              T-Lync posts <Code>application/x-www-form-urlencoded</Code> to{' '}
              <Code>{'/v1/callbacks/tlync/{merchant_id}'}</Code>. PayHub verifies
              the callback token, parses the body (handles both bare HTTP and form
              callbacks), matches by <Code>merchant_ref</Code> — T-Lync echoes
              that, not their own ID — and emits the outbound webhook to your
              endpoint.
            </>
          ),
        },
      ]}
      pitfalls={[
        'success_url is fired by the customer; the form callback is fired by T-Lync. Treat them as separate events. Some browsers fire success_url before the callback lands — design for it.',
        'T-Lync rejects long merchant_order_ref values. Cap to ~32 chars and add the long version in metadata.',
        'Callback token rotation: when you rotate, do it during a low-traffic window. PayHub accepts the new token immediately; T-Lync may take a minute to propagate.',
      ]}
    />
  );
}

// ---------- Adfali ----------

function AdfaliGuide() {
  return (
    <PspBody
      psp="adfali"
      flow="OTP — roadmap"
      icon={Building2}
      summary={
        <>
          Adfali is on the v1.x roadmap — the adapter is registered as a stub so
          the orchestrator, dashboards, and audit log already understand the
          PSP code. Initiates against Adfali return <Code>501 Not Implemented</Code>
          today.
        </>
      }
      credentials={[]}
      steps={[
        {
          title: 'When Adfali ships',
          body: (
            <>
              Expect an <Code>otp_required</Code> flow shape similar to Sadad
              (BCD-issued OTP, customer phone or card-bound). The credential
              schema lands on the merchant portal <em>Gateways</em> tab the
              moment the adapter goes from stub to live; no client changes are
              required to start configuring it.
            </>
          ),
        },
      ]}
      pitfalls={[
        'Don\'t list Adfali in your customer-facing checkout selector until your PayHub deployment reports flow="otp" instead of the placeholder. The /admin /event-catalog page surfaces the live capabilities.',
      ]}
    />
  );
}

// ---------- shared layout ----------

interface CredField {
  name: string;
  detail: string;
  kind?: 'secret' | 'number' | 'text';
}

interface Step {
  title: string;
  body: React.ReactNode;
}

function PspBody({
  psp,
  flow,
  summary,
  credentials,
  steps,
  pitfalls,
  icon: Icon,
}: {
  psp: PspCode;
  flow: string;
  summary: React.ReactNode;
  credentials: CredField[];
  steps: Step[];
  pitfalls: string[];
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="space-y-5 pt-2">
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card/50 p-4 sm:flex-row sm:items-center">
        <PspLogo psp={psp} size="lg" />
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <Badge variant="secondary">{flow}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">{summary}</p>
        </div>
      </div>

      {credentials.length > 0 && (
        <section>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <KeyRound className="h-4 w-4 text-primary" /> Credentials to enter in PayHub
          </h3>
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Field</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {credentials.map((c) => (
                  <tr key={c.name}>
                    <td className="px-3 py-2 font-mono text-xs">{c.name}</td>
                    <td className="px-3 py-2 text-xs">
                      <Badge variant={c.kind === 'secret' ? 'destructive' : 'outline'}>
                        {c.kind ?? 'text'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{c.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <TerminalSquare className="h-4 w-4 text-primary" /> Backend implementation steps
        </h3>
        <ol className="space-y-3">
          {steps.map((s, i) => (
            <li
              key={s.title}
              className="rounded-md border border-border bg-card/50 p-3 text-sm leading-relaxed text-muted-foreground"
            >
              <div className="mb-1.5 flex items-center gap-2 font-semibold text-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                {s.title || `Step ${i + 1}`}
              </div>
              <div className="[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12px] [&_code]:text-foreground">
                {s.body}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {pitfalls.length > 0 && (
        <section>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Pitfalls
          </h3>
          <ul className="list-disc space-y-1.5 ps-5 text-sm leading-relaxed text-muted-foreground">
            {pitfalls.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Item({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[180px_1fr] sm:gap-4">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Icon className="h-4 w-4 text-primary" />
        {label}
      </div>
      <div className="text-sm leading-relaxed text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12px] [&_code]:text-foreground">
        {children}
      </div>
    </div>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <code>{children}</code>;
}

function Pre({ children }: { children: React.ReactNode }) {
  return (
    <pre className="mt-2 overflow-x-auto rounded-md border border-border bg-muted/40 p-3 text-[12px] leading-relaxed text-foreground">
      <code className="!bg-transparent !p-0">{children}</code>
    </pre>
  );
}
